import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";

import { useCart } from "../../components/CartContext";
import "./css/CheckoutScreen.css";

function CheckoutScreen() {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();

  const {
    cart,
    cartCount,
    cartTotal,
  } = useCart();

  const [priceSummary, setPriceSummary] = useState({
    subtotal: 0,
    tax: 0,
    serviceCharge: 0,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /*
  =====================================================
  FETCH PRICE SUMMARY
  =====================================================
  */

  useEffect(() => {
    const loadPriceSummary = async () => {
      if (!cart || cart.length === 0) {
        setLoading(false);

        setPriceSummary({
          subtotal: 0,
          tax: 0,
          serviceCharge: 0,
          total: 0,
        });

        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.post(
          `/api/restaurants/${restaurantId}/price-summary/`,
          {
            items: cart.map((item) => ({
              menu_item_id: item.id,
              quantity: item.quantity,
            })),
          }
        );

        setPriceSummary({
          subtotal: Number(response.data.subtotal || 0),
          tax: Number(response.data.tax || 0),
          serviceCharge: Number(
            response.data.service_charge ||
            response.data.serviceCharge ||
            0
          ),
          total: Number(response.data.total || 0),
        });
      } catch (err) {
        console.error(
          "Price summary error:",
          err
        );

        setError(
          err.response?.data?.detail ||
          err.response?.data ||
          "Unable to calculate order total."
        );

        /*
        Fallback to cart total
        */

        setPriceSummary({
          subtotal: Number(cartTotal || 0),
          tax: 0,
          serviceCharge: 0,
          total: Number(cartTotal || 0),
        });
      } finally {
        setLoading(false);
      }
    };

    loadPriceSummary();
  }, [
    restaurantId,
    cart,
    cartTotal,
  ]);

  /*
  =====================================================
  BACK
  =====================================================
  */

  const handleBack = () => {
    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );
  };

  /*
  =====================================================
  CONTINUE TO ORDER
  =====================================================
  */

  const handleContinue = () => {
    if (!cart || cart.length === 0) {
      return;
    }

    navigate(
      `/${restaurantId}/table/${tableId}/order`,
      {
        state: {
          subtotal: priceSummary.subtotal,
          tax: priceSummary.tax,
          serviceCharge:
            priceSummary.serviceCharge,
          total: priceSummary.total,
        },
      }
    );
  };

  /*
  =====================================================
  EMPTY CART
  =====================================================
  */

  if (!cart || cart.length === 0) {
    return (
      <div className="theme3-checkout-page">

        <div className="theme3-checkout-container">

          <button
            type="button"
            className="theme3-checkout-back"
            onClick={handleBack}
          >
            <span>←</span>
            BACK TO CART
          </button>

          <div className="theme3-checkout-empty">

            <div className="theme3-checkout-empty-icon">
              🛒
            </div>

            <h1>
              YOUR CART IS EMPTY
            </h1>

            <p>
              Add some delicious dishes before
              continuing to checkout.
            </p>

            <button
              type="button"
              className="theme3-checkout-primary-button"
              onClick={() =>
                navigate(
                  `/${restaurantId}/table/${tableId}`
                )
              }
            >
              EXPLORE MENU
            </button>

          </div>

        </div>

      </div>
    );
  }

  return (
    <div className="theme3-checkout-page">

      <div className="theme3-checkout-container">

        {/* =================================================
            BACK
        ================================================= */}

        <button
          type="button"
          className="theme3-checkout-back"
          onClick={handleBack}
        >
          <span>←</span>
          BACK TO CART
        </button>


        {/* =================================================
            HEADER
        ================================================= */}

        <div className="theme3-checkout-heading">

          <span className="theme3-checkout-eyebrow">
            STEP 01
          </span>

          <h1>
            CHECKOUT
          </h1>

          <p>
            Review your order before continuing.
          </p>

        </div>


        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <div className="theme3-checkout-layout">


          {/* =================================================
              LEFT — ITEMS
          ================================================= */}

          <div className="theme3-checkout-items-section">

            <div className="theme3-checkout-section-heading">

              <div>
                <span>
                  YOUR SELECTION
                </span>

                <h2>
                  ORDER ITEMS
                </h2>
              </div>

              <span className="theme3-checkout-item-count">
                {cartCount}{" "}
                {cartCount === 1
                  ? "ITEM"
                  : "ITEMS"}
              </span>

            </div>


            <div className="theme3-checkout-items">

              {cart.map((item, index) => (

                <div
                  className="theme3-checkout-item"
                  key={item.id}
                >

                  {/* IMAGE */}

                  <div className="theme3-checkout-item-image-wrapper">

                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="theme3-checkout-item-image"
                      />
                    ) : (
                      <div className="theme3-checkout-item-image-placeholder">
                        <span>✦</span>
                      </div>
                    )}

                    <span className="theme3-checkout-item-number">
                      {String(index + 1).padStart(
                        2,
                        "0"
                      )}
                    </span>

                  </div>


                  {/* DETAILS */}

                  <div className="theme3-checkout-item-details">

                    <h3>
                      {item.name}
                    </h3>

                    {item.description && (
                      <p>
                        {item.description}
                      </p>
                    )}

                    <div className="theme3-checkout-item-meta">

                      <span>
                        QTY{" "}
                        <strong>
                          {item.quantity}
                        </strong>
                      </span>

                      <span>
                        ₹
                        {(
                          Number(item.price) *
                          Number(item.quantity)
                        ).toFixed(2)}
                      </span>

                    </div>

                  </div>

                </div>

              ))}

            </div>

          </div>


          {/* =================================================
              RIGHT — SUMMARY
          ================================================= */}

          <aside className="theme3-checkout-summary">

            <div className="theme3-checkout-summary-top">

              <span>
                ORDER SUMMARY
              </span>

              <div className="theme3-checkout-summary-line"></div>

            </div>


            {/* SUBTOTAL */}

            <div className="theme3-checkout-summary-row">

              <span>
                SUBTOTAL
              </span>

              <strong>
                ₹
                {priceSummary.subtotal.toFixed(2)}
              </strong>

            </div>


            {/* TAX */}

            <div className="theme3-checkout-summary-row">

              <span>
                TAX
              </span>

              <strong>
                ₹
                {priceSummary.tax.toFixed(2)}
              </strong>

            </div>


            {/* SERVICE CHARGE */}

            <div className="theme3-checkout-summary-row">

              <span>
                SERVICE CHARGE
              </span>

              <strong>
                ₹
                {priceSummary.serviceCharge.toFixed(
                  2
                )}
              </strong>

            </div>


            {/* DIVIDER */}

            <div className="theme3-checkout-summary-divider"></div>


            {/* TOTAL */}

            <div className="theme3-checkout-total-row">

              <div>
                <span>
                  TOTAL
                </span>

                <small>
                  INCLUDING ALL CHARGES
                </small>
              </div>

              <strong>
                ₹
                {priceSummary.total.toFixed(2)}
              </strong>

            </div>


            {/* ERROR */}

            {error && (
              <div className="theme3-checkout-error">
                {error}
              </div>
            )}


            {/* CONTINUE */}

            <button
              type="button"
              className="theme3-checkout-primary-button theme3-checkout-continue-button"
              onClick={handleContinue}
              disabled={loading}
            >

              {loading ? (
                <>
                  <span className="theme3-checkout-spinner"></span>
                  CALCULATING...
                </>
              ) : (
                <>
                  CONTINUE TO ORDER
                  <span>→</span>
                </>
              )}

            </button>


            <p className="theme3-checkout-secure-text">
              Your order will be reviewed on the
              next step before it is created.
            </p>

          </aside>

        </div>

      </div>

    </div>
  );
}

export default CheckoutScreen;